import React, { useEffect, useState, useRef } from 'react';
import './GoogleSignIn.css';

const GoogleSignIn = ({ onSignIn }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    setIsComponentMounted(true);
    
    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsGoogleLoaded(true);
    };
    script.onerror = () => {
      setError('Erro ao carregar o Google Sign-In. Verifique sua conexão com a internet.');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      setIsComponentMounted(false);
    };
  }, []);

  useEffect(() => {
    // Only initialize when both Google is loaded and component is mounted
    if (isGoogleLoaded && isComponentMounted && buttonRef.current) {
      initializeGoogleSignIn();
    }
  }, [isGoogleLoaded, isComponentMounted]);

  const initializeGoogleSignIn = () => {
    if (window.google && window.google.accounts && buttonRef.current) {
      try {
        window.google.accounts.id.initialize({
          client_id: '629008524613-bnrm5o3avoi71u1gvk58vtlubk1tl275.apps.googleusercontent.com',
          callback: handleCredentialResponse,
          auto_select: false,
          // hosted_domain: "your-domain.com" // Optional: restrict to specific domain
        });

        // Render the sign-in button
        window.google.accounts.id.renderButton(
          buttonRef.current,
          { 
            theme: 'outline', 
            size: 'large', 
            text: 'signin_with',
            width: 300,
            type: 'standard'
          }
        );

        // Optional: Enable One Tap
        // window.google.accounts.id.prompt();
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
        setError('Erro ao inicializar o Google Sign-In. Verifique a configuração do Client ID.');
      }
    }
  };

  const handleCredentialResponse = async (response) => {
    try {
      setIsLoading(true);
      setError(null);

      const idToken = response.credential; // JWT from Google
      
      // Store the JWT token in sessionStorage
      sessionStorage.setItem('id_token', idToken);
      
      // Decode the JWT to get user info (without verification for display purposes)
      const userInfo = decodeJWT(idToken);
      
      if (!userInfo) {
        throw new Error('Invalid token received from Google');
      }
      
      // Store user info in sessionStorage
      sessionStorage.setItem('user_info', JSON.stringify(userInfo));
      
      // Call the onSignIn callback with user info
      if (onSignIn) {
        onSignIn(userInfo, idToken);
      }

      console.log('Successfully signed in:', userInfo);
      
    } catch (error) {
      console.error('Sign-in error:', error);
      setError('Erro ao fazer login. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

  const handleSignOut = () => {
    // Clear session storage
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('user_info');
    
    // Sign out from Google
    if (window.google && window.google.accounts) {
      window.google.accounts.id.disableAutoSelect();
    }
    
    // Reload the page or redirect to login
    window.location.reload();
  };

  const handleManualSignIn = () => {
    // For development/testing purposes
    const mockUserInfo = {
      name: 'Test User',
      email: 'test@example.com',
      picture: null,
      sub: '123456789'
    };
    
    const mockToken = 'mock-jwt-token';
    
    sessionStorage.setItem('id_token', mockToken);
    sessionStorage.setItem('user_info', JSON.stringify(mockUserInfo));
    
    if (onSignIn) {
      onSignIn(mockUserInfo, mockToken);
    }
  };

  return (
    <div className="google-signin-container">
      <div className="signin-card">
        <div className="signin-header">
          <h1>Item4</h1>
          <p>Faça login para continuar</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <div className="error-help">
              <p><strong>Para desenvolvedores:</strong></p>
              <ol>
                <li>Verifique se o Google Client ID está configurado corretamente</li>
                <li>Certifique-se de que o domínio está autorizado no Google Cloud Console</li>
                <li>Verifique se a API do Google+ está habilitada</li>
              </ol>
            </div>
          </div>
        )}

        <div className="signin-content">
          {isGoogleLoaded && isComponentMounted ? (
            <div ref={buttonRef} className="google-signin-button"></div>
          ) : (
            <div className="loading-google">
              <div className="spinner"></div>
              <span>Carregando Google Sign-In...</span>
            </div>
          )}
          
          {isLoading && (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <span>Fazendo login...</span>
            </div>
          )}

          {/* Development mode - Manual sign-in button */}
          {process.env.NODE_ENV === 'development' && (
            <div className="dev-signin">
              <button 
                className="dev-signin-btn"
                onClick={handleManualSignIn}
              >
                🔧 Login de Desenvolvimento
              </button>
              <small>Use este botão para testar sem Google OAuth</small>
            </div>
          )}
        </div>

        <div className="signin-footer">
          <p>Entre com sua conta Google</p>
        </div>
      </div>
    </div>
  );
};

export default GoogleSignIn;
